// src/pages/FamilyFundPage.tsx

import React, { useState, useEffect } from 'react';
import { penaltiesApi } from '../api';
import { useAuth } from '../context/AuthContext';
import type { FamilyFund } from '../types';

function firstDayOfMonth(): string {
  const d = new Date(Date.now() + 7*3600000);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
}
function lastDayOfMonth(): string {
  const d = new Date(Date.now() + 7*3600000);
  const last = new Date(d.getFullYear(), d.getMonth()+1, 0);
  return last.toISOString().split('T')[0];
}

export default function FamilyFundPage() {
  const { user } = useAuth();
  const [from, setFrom]     = useState(firstDayOfMonth());
  const [to, setTo]         = useState(lastDayOfMonth());
  const [data, setData]     = useState<FamilyFund | null>(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { setData(await penaltiesApi.getFamily(from, to)); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [from, to]);

  const settle = async (userId: string) => {
    setSettling(userId);
    try { await penaltiesApi.settle(userId, from, to); await load(); } catch {}
    setSettling(null);
  };

  return (
    <div>
      <div style={{ fontSize:26, fontWeight:600, letterSpacing:-0.5, marginBottom:24 }}>Family Fund</div>

      {/* Date range */}
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:20, flexWrap:'wrap' }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          style={inputStyle} />
        <span style={{ color:'var(--text3)', fontSize:12 }}>to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          style={inputStyle} />
      </div>

      {loading ? (
        <div style={{ color:'var(--text3)', fontSize:12, fontFamily:'var(--font-mono)' }}>loading...</div>
      ) : !data ? null : (
        <>
          {/* Total cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginBottom:20 }}>
            <StatCard label="Total Penalties" value={`IDR ${data.grandTotal.toLocaleString()}`} color="var(--text)" />
            <StatCard label="Unsettled" value={`IDR ${data.grandUnsettled.toLocaleString()}`} color="var(--red)" />
            <StatCard label="Settled" value={`IDR ${data.grandSettled.toLocaleString()}`} color="var(--green)" />
          </div>

          {/* Per member */}
          {data.members.map(m => (
            <div key={m.userId} className="card" style={{ marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:600 }}>{m.userName}</div>
                  <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--font-mono)', marginTop:2 }}>
                    {m.penalties.length} slip{m.penalties.length !== 1 ? 's' : ''} · IDR {m.total.toLocaleString()} total
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  {m.unsettled > 0 && (
                    <span style={{ fontSize:11, color:'var(--red)', fontFamily:'var(--font-mono)' }}>
                      IDR {m.unsettled.toLocaleString()} unsettled
                    </span>
                  )}
                  {user?.role === 'admin' && m.unsettled > 0 && (
                    <button onClick={() => settle(m.userId)} disabled={settling === m.userId}
                      style={{
                        padding:'6px 14px', borderRadius:'var(--radius-sm)', fontSize:11,
                        background:'var(--green-dim)', border:'1px solid rgba(0,229,160,0.2)',
                        color:'var(--green)', cursor:'pointer',
                      }}>
                      {settling === m.userId ? 'settling...' : 'Mark settled'}
                    </button>
                  )}
                  <button onClick={() => penaltiesApi.exportCsv(from, to, m.userId)}
                    style={{ padding:'6px 12px', borderRadius:'var(--radius-sm)', fontSize:11,
                      background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)', cursor:'pointer' }}>
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Penalty list */}
              {m.penalties.slice(0, 5).map(p => (
                <div key={p.penaltyId} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'7px 0', borderTop:'1px solid var(--border)', fontSize:11,
                }}>
                  <div>
                    <span style={{ color:'var(--text2)' }}>{p.habitName}</span>
                    <span style={{ color:'var(--text3)', marginLeft:8, fontFamily:'var(--font-mono)' }}>{p.date}</span>
                  </div>
                  <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <span style={{ fontFamily:'var(--font-mono)', color:'var(--red)' }}>
                      IDR {p.penaltyAmount.toLocaleString()} ×{p.slipCount}
                    </span>
                    {p.settled
                      ? <span style={{ fontSize:9, color:'var(--green)' }}>settled</span>
                      : <span style={{ fontSize:9, color:'var(--text3)' }}>pending</span>}
                  </div>
                </div>
              ))}
              {m.penalties.length > 5 && (
                <div style={{ fontSize:10, color:'var(--text3)', marginTop:6, fontFamily:'var(--font-mono)' }}>
                  +{m.penalties.length - 5} more — export CSV for full list
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding:'7px 12px', borderRadius:'var(--radius-sm)', fontSize:12,
  background:'var(--bg3)', border:'1px solid var(--border2)', color:'var(--text)',
  fontFamily:'var(--font-mono)', outline:'none',
};

function StatCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="card">
      <div style={{ fontSize:9, fontWeight:700, color:'var(--text3)', letterSpacing:'1px',
        textTransform:'uppercase', fontFamily:'var(--font-mono)', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:700, color, fontFamily:'var(--font-mono)' }}>{value}</div>
    </div>
  );
}
